import { inject, Injectable } from '@angular/core';
import { CurrentUserService } from '@rma/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { ChatApiService } from '@rma/generic/orc/intercom';
import { Environment } from '@rma/generic/util/environment';
import { HeapService } from '@rma/generic/util/heap';
import { HotjarService } from '@rma/generic/util/hotjar';
import { HubSpotService } from '@rma/generic/util/hubspot';
import { nonNullable } from '@rma/generic/util/operators/non-nullable';
import { retryWithDelay } from '@rma/generic/util/operators/retry-with-delay';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { throwIf } from '@rma/generic/util/operators/throw-if';
import { catchError, combineLatest, EMPTY, filter, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyUserIdentificationService {
  private readonly chatApiService = inject(ChatApiService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly userService = inject(CurrentUserService);
  private readonly environment = inject(Environment);

  private readonly heapService = inject(HeapService, { optional: true });
  private readonly hotjarService = inject(HotjarService, { optional: true });
  private readonly hubSpotService = inject(HubSpotService, { optional: true });

  private readonly currentUser$ = this.userService.currentUser$.pipe(rmaShareReplay());

  private readonly userData$ = combineLatest({
    user: this.currentUser$.pipe(
      nonNullable(),
      filter((c) => !!c.userId),
    ),
    userChatData: this.chatApiService.userChatData$,
  }).pipe(rmaShareReplay());

  public initialise() {
    this.initialiseHeap();
    this.initialiseHotjar();
    this.initialiseHubSpot();
  }

  private initialiseHeap() {
    if (this.heapService && this.environment.heapAppId) {
      this.heapService.initialiseHeap(this.environment.heapAppId);

      // addDynamicUserProperties
      this.userData$.subscribe(({ user, userChatData }) => {
        // because we don't handle log-out, before we identify anybody, lets reset the ident
        // this makes sure we don't join two users together, but if we just re-ident as the same user anyway, things will be fine so 🧙‍♀️
        this.heapService?.resetIdentity();

        if (!user.isImpersonating) {
          this.heapService?.identify(user.userId);

          const { user_id, user_hash, app_id, rma_locale, ...userProperties } = userChatData;

          // If you ever get `TypeError: window.heap.push is not a function` ---> https://github.com/segmentio/analytics.js/issues/605
          this.heapService?.addUserProperties({ ...userProperties, rma_user_type: user.userType });
        }

        this.heapService?.addUserProperties({ 'rma.authenticatedViaRadmin': user.isImpersonating });
      });

      // setEventProperties
      this.currentSubscriptionService.currentSubscriptionSummary$
        .pipe(map((s) => ({ rma_sub_is_free: s?.isFreeSubscription, rma_sub_tier: s?.subscriptionTier })))
        .subscribe((summary) => this.heapService?.addEventProperties(summary));
    }
  }

  private initialiseHotjar() {
    if (this.hotjarService && this.environment.hotjarId) {
      this.userData$
        .pipe(
          throwIf(
            () => !this.hotjarService?.window.hj,
            () => 'hotjar not loaded yet',
          ),
          retryWithDelay(200, 4),
          catchError((error) => {
            console.log(error);
            return EMPTY;
          }),
        )
        .subscribe(({ user, userChatData }) => {
          if (!user.isImpersonating) {
            const { user_id, user_hash, app_id, ...userProperties } = userChatData;

            this.hotjarService?.identify(`${userChatData['user_id']}`, {
              ...userProperties,
              'rma.authenticatedViaRadmin': user.isImpersonating,
              rma_user_type: user.userType,
            });
          }
        });
    }
  }

  private initialiseHubSpot() {
    if (this.hubSpotService) {
      const user$ = this.currentUser$.pipe(
        nonNullable(),
        map(({ email, isImpersonating, userId }) => ({ email, isImpersonating, userId })),
        throwIf(
          () => !this.hubSpotService?.window?._hsq,
          () => 'hubspot not loaded yet',
        ),
        retryWithDelay(200, 4),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
        rmaShareReplay(),
      );

      // identify user
      user$.subscribe(({ email, isImpersonating, userId }) => {
        // remove existing tracking info
        this.hubSpotService?.window?._hsq.push(['revokeCookieConsent']);

        if (this.hubSpotService?.trackUser(isImpersonating, userId)) {
          this.hubSpotService?.window?._hsq.push(['identify', { email }]);
        }
      });

      this.hubSpotService.initialiseHubspot(user$.pipe(map(({ isImpersonating, userId }) => ({ isImpersonating, userId }))));
    }
  }
}
